import { computeEvaluation } from "scoring/scoringEval";

// --- CERAD-Plus norm regression (Korrektur sheet of CERAD-Plus_Exemple.xls) ---
//
// Each scale score goes through: raw → transform (normalizing) → z-score
// (age/education/sex-adjusted linear regression norm).
//
//   z = (T - (q + b_age*age + b_edu*edu + b_sex*sex
//          + b_eduXsex*(edu-12.5)*sex
//          + b_eduXage*(edu-12.5)*(age-68.69)
//          + b_ageXsex*(age-68.69)*sex))
//       / (m + m_age*age)
//
// where T is the transformed scale score. 12.5 / 68.69 are the fixed
// centering constants baked into the original spreadsheet formulas.
const EDU_CENTER = 12.5;
const AGE_CENTER = 68.69;

const KORREKTUR = {
  SemFl: { q: 28.162, m: 5.18, age: -0.175, edu: 0.4, sex: 1.218, eduXsex: 0.492, eduXage: 0, ageXsex: 0, mAge: 0 },
  Boston: { q: 1.398, m: 0.1905, age: -0.0048, edu: 0.0149, sex: -0.031, eduXsex: 0, eduXage: 0, ageXsex: -0.0026, mAge: 0 },
  MMS: { q: 1.534, m: 0.1154, age: -0.0055, edu: 0.0096, sex: 0.0374, eduXsex: 0, eduXage: 0, ageXsex: 0.0023, mAge: 0 },
  WL_Lern: { q: 27.82, m: 3.17, age: -0.161, edu: 0.214, sex: 2.823, eduXsex: 0, eduXage: 0, ageXsex: 0.0518, mAge: 0 },
  WL_Abruf: { q: 1.374, m: 0.1917, age: -0.0086, edu: 0.0145, sex: 0.14, eduXsex: 0, eduXage: 0, ageXsex: 0.0026, mAge: 0 },
  WL_Intru: { q: 0.962, m: 0.9592, age: -0.0099, edu: 0.0166, sex: 0.171, eduXsex: 0.0409, eduXage: 0, ageXsex: 0, mAge: 0 },
  WL_Save: { q: 1031.179, m: -4.57906, age: -6.029, edu: 13.491, sex: 64.414, eduXsex: 0, eduXage: 0, ageXsex: 0, mAge: 3.01459 },
  Diskri: { q: 1.547, m: 0.1954, age: -0.0039, edu: 0.0089, sex: 0.0815, eduXsex: 0, eduXage: 0, ageXsex: 0, mAge: 0 },
  Fig_abz: { q: 1.461, m: 0.1777, age: -0.0025, edu: 0.011, sex: -0.0524, eduXsex: 0.009, eduXage: 0, ageXsex: 0, mAge: 0 },
  Fig_abr: { q: 1.459, m: 0.0613269, age: -0.0097, edu: 0.0191, sex: -0.0894, eduXsex: 0.0206, eduXage: 0, ageXsex: 0, mAge: 0.002797 },
  Fig_Sav: { q: 0.187, m: 0.2402, age: -0.0071, edu: 0.01, sex: -0.0535, eduXsex: 0.0117, eduXage: 0, ageXsex: 0, mAge: 0 },
  SWorter: { q: 7.233816001493, m: 1.962274451921, age: -0.05748836469117, edu: 0.235221944626, sex: 0.6869018196729, eduXsex: 0, eduXage: 0, ageXsex: 0, mAge: 0 },
  Trail_A: { q: 1.596962521447, m: -0.04544983423313, age: 0.002057164498667, edu: -0.001583272703407, sex: 0, eduXsex: 0, eduXage: 0, ageXsex: 0, mAge: 0 },
  Trail_B: { q: 1.672906779719, m: -0.0268554159741, age: 0.001404391122202, edu: -0.003196964105159, sex: -0.005937102761671, eduXsex: 0, eduXage: 0, ageXsex: 0, mAge: 0 },
  TrailBA: { q: 0.7955769473821, m: -0.2195931628381, age: 0.003018003542806, edu: -0.01998601443641, sex: -0.03975949056036, eduXsex: 0, eduXage: 0, ageXsex: 0, mAge: 0 },
};

// Normalizing transform applied to each scale's raw score before the
// regression norm (arcsine, log, Box-Cox, power — chosen per variable in
// the original CERAD-Plus template to make its distribution more normal).
const TRANSFORM = {
  SemFl: (v) => v,
  Boston: (v) => Math.asin(v / 15.1),
  MMS: (v) => Math.asin(v / 30.3),
  WL_Lern: (v) => v,
  WL_Abruf: (v) => Math.asin(Math.sqrt((v + 1) / 11.25)),
  WL_Intru: (v) => -Math.log(v + 0.3),
  WL_Save: (v) => Math.pow(v, 1.5),
  Diskri: (v) => Math.asin(v / 100),
  Fig_abz: (v) => Math.asin(Math.sqrt(v / 11)),
  Fig_abr: (v) => Math.asin(v / 11.2),
  Fig_Sav: (v) => Math.pow(Math.abs(v / 100 - 1), 0.7) * Math.sign(v - 99.999999),
  SWorter: (v) => (Math.pow(v, 0.69) - 1) / 0.69,
  Trail_A: (v) => (Math.pow(v, -0.49) - 1) / -0.49,
  Trail_B: (v) => (Math.pow(v, -0.53) - 1) / -0.53,
  TrailBA: (v) => (Math.pow(v, -0.42) - 1) / -0.42,
};

// patientInfo.genre is "H" (homme/male) / "F" (femme/female) in this app;
// the CERAD-Plus norms code sex as 0=m, 1=w.
export const genreToSex = (genre) => (genre === "H" ? 0 : genre === "F" ? 1 : null);

export const roundZ = (z) => (z == null || !Number.isFinite(z) ? null : Math.round(z * 10) / 10);

/**
 * Age/education/sex-adjusted z-score for one CERAD-Plus scale.
 * @param {string} key - one of the KORREKTUR/TRANSFORM keys above
 * @param {number} scaleValue - raw (untransformed) scale score
 * @param {{age:number, edu:number, sex:0|1}} ctx
 * @returns {number|null}
 */
export function ceradZScore(key, scaleValue, ctx) {
  if (scaleValue == null || !Number.isFinite(scaleValue)) return null;
  const { age, edu, sex } = ctx || {};
  if (!Number.isFinite(age) || !Number.isFinite(edu) || (sex !== 0 && sex !== 1)) return null;
  const k = KORREKTUR[key];
  const f = TRANSFORM[key];
  if (!k || !f) return null;
  const t = f(scaleValue);
  if (!Number.isFinite(t)) return null;
  const predicted =
    k.q + k.age * age + k.edu * edu + k.sex * sex
    + k.eduXsex * (edu - EDU_CENTER) * sex
    + k.eduXage * (edu - EDU_CENTER) * (age - AGE_CENTER)
    + k.ageXsex * (age - AGE_CENTER) * sex;
  const denom = k.m + k.mAge * age;
  if (!denom) return null;
  return (t - predicted) / denom;
}

const sum = (...vals) => {
  if (vals.some((v) => !Number.isFinite(v))) return null;
  return vals.reduce((s, v) => s + v, 0);
};

// --- Per-scale composition helpers (raw inputs → scale score → z) ---

export const ceradSemFlZ = (total, ctx) => ceradZScore("SemFl", total, ctx);
export const ceradBostonZ = (total, ctx) => ceradZScore("Boston", total, ctx);
export const ceradMMSZ = (total, ctx) => ceradZScore("MMS", total, ctx);
export const ceradSWorterZ = (total, ctx) => ceradZScore("SWorter", total, ctx);
export const ceradTrailAZ = (timeSec, ctx) => ceradZScore("Trail_A", timeSec, ctx);
export const ceradTrailBZ = (timeSec, ctx) => ceradZScore("Trail_B", timeSec, ctx);

export const ceradTrailBAZ = (timeASec, timeBSec, ctx) => {
  if (!Number.isFinite(timeASec) || timeASec <= 0 || !Number.isFinite(timeBSec)) return null;
  return ceradZScore("TrailBA", timeBSec / timeASec, ctx);
};

export const ceradFigAbzZ = (kreis, rhombus, rechtecke, wuerfel, ctx) => {
  const total = sum(kreis, rhombus, rechtecke, wuerfel);
  return { total, z: ceradZScore("Fig_abz", total, ctx) };
};

// Figures recall combines items 1-4 only (item 5 is excluded from the sum
// in the original CERAD-Plus formula — replicated faithfully here).
export const ceradFigAbrZ = (item1, item2, item3, item4, ctx) => {
  const total = sum(item1, item2, item3, item4);
  return { total, z: ceradZScore("Fig_abr", total, ctx) };
};

export const ceradFigSavZ = (figAbrTotal, figAbzTotal, ctx) => {
  if (!Number.isFinite(figAbrTotal) || !Number.isFinite(figAbzTotal) || figAbzTotal === 0) return null;
  return ceradZScore("Fig_Sav", (figAbrTotal / figAbzTotal) * 100, ctx);
};

export const ceradWLLernZ = (r1, r2, r3, ctx) => {
  const total = sum(r1, r2, r3);
  return { total, z: ceradZScore("WL_Lern", total, ctx) };
};

export const ceradWLIntruZ = (i1, i2, i3, abrufIntru, ctx) => {
  const total = sum(i1, i2, i3, abrufIntru);
  return ceradZScore("WL_Intru", total, ctx);
};

export const ceradWLAbrufZ = (richtige, ctx) => ceradZScore("WL_Abruf", richtige, ctx);

export const ceradWLSaveZ = (abrufRichtige, lernTrial3, ctx) => {
  if (!Number.isFinite(abrufRichtige) || !Number.isFinite(lernTrial3) || lernTrial3 === 0) return null;
  return ceradZScore("WL_Save", (abrufRichtige / lernTrial3) * 100, ctx);
};

export const ceradDiskriZ = (richtigeJa, richtigeNein, ctx) => {
  const total = sum(richtigeJa, richtigeNein);
  if (total == null) return null;
  return ceradZScore("Diskri", total * 5, ctx);
};

// --- Legacy path: auto-fill from an official CERAD-Plus report export ---
//
// `rows` come from the Electron-side parser of an uploaded CERAD-Plus xls
// (already-computed z-values per Profil-sheet row 1-15). Returns one entry
// per granular scoresheet test, so callers can updateTest() each in turn.
const findRow = (ceradRows, num) => ceradRows.find((r) => r.num === num);
const roundResultZ = (z) => Math.round(z * 10) / 10;

const ceradXlsMapping = [
  { testKey: "TMT A (CERAD, 2005 / GREFEX, 2008)", resultLoc: "G14", rows: [{ num: 13, label: "Temps z" }] },
  { testKey: "Fluences Mots M (CERAD, 2005)", resultLoc: "G27", rows: [{ num: 12, label: "z" }] },
  { testKey: "Fluences Animaux (CERAD, 2005)", resultLoc: "G28", rows: [{ num: 1, label: "z" }] },
  {
    testKey: "TMT B (CERAD, 2005 / GREFEX, 2008)",
    resultLoc: "G29",
    rows: [
      { num: 14, label: "Temps z" },
      { num: 15, label: "B/A z", italic: true },
    ],
  },
  {
    testKey: "10 mots (CERAD, 2005)",
    resultLoc: "G45",
    rows: [
      { num: 4, label: "Σ Essais1-3 z" },
      { num: 6, label: "Intrusions z", italic: true },
    ],
  },
  {
    testKey: "10 mots (CERAD, 2005) (Row 48)",
    resultLoc: "G46",
    rows: [
      { num: 5, label: "Score brut z" },
      { num: 7, label: "Rétention z" },
    ],
  },
  { testKey: "10 mots (CERAD, 2005) (Row 49)", resultLoc: "G47", rows: [{ num: 8, label: "Discriminabilité z" }] },
  {
    testKey: "Rappel de figures (CERAD, 2005)",
    resultLoc: "G73",
    rows: [
      { num: 10, label: "Score brut z" },
      { num: 11, label: "Rétention z" },
    ],
  },
  { testKey: "Copie de figures (CERAD, 2005)", resultLoc: "G86", rows: [{ num: 9, label: "z" }] },
  { testKey: "Boston Naming Test (CERAD, 2005)", resultLoc: "G108", rows: [{ num: 2, label: "z" }] },
  { testKey: "MMSE (CERAD, 2005)", resultLoc: "G117", rows: [{ num: 3, label: "z" }], includeValueMax: true },
];

/**
 * Distribute an already-scored CERAD-Plus report (uploaded xls) across the
 * granular per-scale test entries. Returns an array of
 * { testKey, resultLoc, resultData, resultText, evaluation }.
 */
export const mapCeradToScoresheet = (ceradRows) => {
  const results = [];

  for (const mapping of ceradXlsMapping) {
    const resultData = [];
    const textParts = [];
    let hasAnyValue = false;

    if (mapping.includeValueMax) {
      const ceradRow = findRow(ceradRows, mapping.rows[0].num);
      if (ceradRow && ceradRow.value != null && ceradRow.max != null) {
        const commentValue = `${ceradRow.value}/${ceradRow.max}`;
        resultData.push({ label: "comment", value: commentValue });
        textParts.push(commentValue);
      }
    }

    for (const rowDef of mapping.rows) {
      const ceradRow = findRow(ceradRows, rowDef.num);
      if (!ceradRow || ceradRow.zWert === "*" || ceradRow.zWert == null) continue;

      const z = roundResultZ(ceradRow.zWert);
      hasAnyValue = true;

      resultData.push({
        label: rowDef.label,
        value: z,
        rawValue: z,
        ...(rowDef.italic ? { italic: true } : {}),
      });
      textParts.push(`${rowDef.label} = ${z}`);
    }

    if (!hasAnyValue) continue;

    const resultText = textParts.join("\n");
    const evaluation = computeEvaluation(resultData, mapping.resultLoc);

    results.push({
      testKey: mapping.testKey,
      resultLoc: mapping.resultLoc,
      resultData,
      resultText,
      evaluation,
    });
  }

  return results;
};
